/**
 * GIF Picker Dialog
 * 
 * Search and select GIFs from GIPHY for posts.
 * Features:
 * - Trending GIFs on open
 * - Debounced search
 * - Grid display with hover effects
 */

import React, { useState, useEffect } from 'react';
import { Smile, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDesign } from '@/contexts/DesignContext';
import { Input } from '@/components/ui/input';
import { API_URL } from '../utils/constants';

/**
 * @param {Object} props
 * @param {boolean} props.open - Dialog visibility state
 * @param {Function} props.onOpenChange - Callback when dialog opens/closes
 * @param {Function} props.onSelect - Callback when a GIF is selected, receives gif object
 */
export function GifPickerDialog({ open, onOpenChange, onSelect }) {
  const { isStudio } = useDesign();
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Search GIFs from GIPHY API
   * @param {string} q - Search query (empty for trending)
   */
  const searchGifs = async (q) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = q 
        ? `${API_URL}/api/comms/v3/gifs/search?query=${encodeURIComponent(q)}&limit=20`
        : `${API_URL}/api/comms/v3/gifs/trending?limit=20`;
      
      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setGifs(data.gifs || []);
        if (data.gifs?.length === 0) {
          setError('No GIFs found');
        }
      } else {
        setError('Couldn\'t load GIFs. Please check your connection.');
      }
    } catch (err) {
      console.error('Error searching gifs:', err);
      setError('Failed to load GIFs');
    } finally {
      setLoading(false);
    }
  };

  // Load trending GIFs when dialog opens
  useEffect(() => {
    if (open) {
      setGifs([]);
      setQuery('');
      searchGifs('');
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (open && query) {
        searchGifs(query);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [query, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isStudio ? 'st-gifdlg max-w-lg max-h-[80vh]' : "max-w-lg max-h-[80vh]"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smile className={isStudio ? 'w-[18px] h-[18px] text-muted-foreground' : "w-5 h-5 text-yellow-500"} />
            Choose a GIF
          </DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search GIFs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-4"
          autoFocus
        />
        <div className="h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading GIFs...</p>
              </div>
            </div>
          ) : error && gifs.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-500 dark:text-slate-400">
                <Smile className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{error}</p>
                {query && <p className="text-xs mt-1">Try a different search term</p>}
              </div>
            </div>
          ) : (
            <div className={isStudio ? 'st-gifgrid grid grid-cols-3 gap-2' : "grid grid-cols-3 gap-2"}>
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => onSelect(gif)}
                  className="aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all bg-slate-100 dark:bg-[#3a3d41]"
                >
                  <img 
                    src={gif.preview} 
                    alt={gif.title || 'GIF'} 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={isStudio ? 'st-gifcap' : "text-center text-xs text-slate-400 dark:text-slate-500 mt-2"}>
          Powered by GIPHY
        </div>
      </DialogContent>
    </Dialog>
  );
}
